"use client";

import type { Dictionary } from "@/core/i18n/__generated__/dictionary";
import type {
  CapturePhase,
  LightingQuality,
} from "@/features/assessments/components/ImageCaptureStep/captureTypes";
import type { FaceStatus } from "@/features/facialRecognition";

type LightingIndicator = {
  color: string;
  text: string;
  icon: "up" | "down" | "check";
  warning: boolean;
  message?: string;
};

type UseStudioUiStateOptions = {
  phase: CapturePhase;
  faceStatus: FaceStatus;
  lightingQuality: LightingQuality;
  t: (key: keyof Dictionary) => string;
};

export const useStudioUiState = ({
  phase,
  faceStatus,
  lightingQuality,
  t,
}: UseStudioUiStateOptions) => {
  const ringColor =
    phase === "ready" || faceStatus === "perfect"
      ? "hsl(var(--primary))"
      : faceStatus === "no-face" || faceStatus === "initializing"
        ? "rgba(255, 255, 255, 0.4)"
        : "hsl(30 80% 60%)";

  // Alpha-reduced version for the soft 20px glow spread (solid ring stays full opacity)
  const ringGlowColor =
    phase === "ready" || faceStatus === "perfect"
      ? "hsl(var(--primary) / 0.3)"
      : faceStatus === "no-face" || faceStatus === "initializing"
        ? "rgba(255, 255, 255, 0.2)"
        : "hsl(30 80% 60% / 0.3)";

  const lightingIndicator = (() => {
    switch (lightingQuality) {
      case "too-dark":
        return {
          color: "text-red-400",
          text: t("assessmentSteps.studioSession.lighting.tooDark"),
          icon: "up",
          warning: true,
          message: t("assessmentSteps.studioSession.lighting.tooDarkMessage"),
        };
      case "dim":
        return {
          color: "text-yellow-400",
          text: t("assessmentSteps.studioSession.lighting.dim"),
          icon: "up",
          warning: true,
          message: t("assessmentSteps.studioSession.lighting.dimMessage"),
        };
      case "good":
        return {
          color: "text-green-400",
          text: t("assessmentSteps.studioSession.lighting.good"),
          icon: "check",
          warning: false,
        };
      case "bright":
        return {
          color: "text-yellow-400",
          text: t("assessmentSteps.studioSession.lighting.bright"),
          icon: "down",
          warning: false,
        };
      case "too-bright":
        return {
          color: "text-red-400",
          text: t("assessmentSteps.studioSession.lighting.tooBright"),
          icon: "down",
          warning: true,
          message: t("assessmentSteps.studioSession.lighting.tooBrightMessage"),
        };
      default:
        return {
          color: "text-green-400",
          text: t("assessmentSteps.studioSession.lighting.good"),
          icon: "check",
          warning: false,
        };
    }
  })() satisfies LightingIndicator;

  const ringLightIntensity = (() => {
    switch (lightingQuality) {
      case "too-dark":
        return 4.0;
      case "dim":
        return 3.0;
      case "good":
        return 0.8;
      case "bright":
        return 0.4;
      case "too-bright":
        return 0.2;
      default:
        return 0.8;
    }
  })();

  const isFaceAligned = faceStatus === "perfect" || phase === "ready";

  return {
    ringColor,
    ringGlowColor,
    ringLightIntensity,
    lightingIndicator,
    isFaceAligned,
  };
};
